import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, InfoWindow, Polygon } from "@react-google-maps/api";
import { db } from "../../firebase/config";
import { collection, onSnapshot, doc, getDoc, query, updateDoc, where } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { createFarmMapIcon, getFarmIconMeta } from "../../utils/farmIcons";
import { geocodeExactLocation } from "../../utils/geocode";
import {
  getFarmStandMapPoint,
  getFarmStandMapType,
  getRenderableUserLocationMarkerPoint,
  isFarmStandMapEligible,
} from "../../utils/mapEntities";
import { getStandPublicLocationLabel } from "../../utils/standLocation";
import { normalizeCoordinates } from "../../utils/locationUtils";
import {
  getEventLocationQuery,
  getEventMapPopupDetails,
} from "../../utils/eventDisplay";

const containerStyle = {
  width: "100%",
  height: "560px",
  borderRadius: "18px",
};

const visualMapCenter = { lat: 39.8283, lng: -98.5795 };

const CATEGORY_OPTIONS = ["hay", "grain", "products", "eggs", "livestock", "equipment"];

const LAND_USE_TYPE_LABELS = {
  horse_grazing: "Horse grazing pasture",
  livestock_grazing: "Livestock grazing",
  fenced_dog_run: "Fenced dog run",
  garden_plot: "Garden plot",
  hay_field_access: "Hay field access",
  equipment_parking: "Equipment parking",
  temporary_holding: "Temporary holding area",
  other: "Other land use",
};

const sqMetersToAcres = (sqMeters) => sqMeters * 0.000247105;

const normalizePoint = (point) => ({
  lat: typeof point.lat === "string" ? Number(point.lat) : point.lat,
  lng: typeof point.lng === "string" ? Number(point.lng) : point.lng,
});

const isValidPoint = (point) =>
  typeof point?.lat === "number" &&
  typeof point?.lng === "number" &&
  !Number.isNaN(point.lat) &&
  !Number.isNaN(point.lng);

const readPathFromPolygon = (polygonInstance) => {
  if (!polygonInstance) return [];

  const path = polygonInstance.getPath();
  const nextPath = [];

  for (let i = 0; i < path.getLength(); i++) {
    const point = path.getAt(i);
    nextPath.push({ lat: point.lat(), lng: point.lng() });
  }

  return nextPath;
};

const fitMapToPath = (map, path, padding = 60) => {
  if (!map || !window.google?.maps || !Array.isArray(path) || path.length === 0) return;

  const bounds = new window.google.maps.LatLngBounds();

  path.forEach((point) => {
    if (isValidPoint(point)) bounds.extend(point);
  });

  if (!bounds.isEmpty()) map.fitBounds(bounds, padding);
};

const fitMapToJob = (map, job) => {
  if (!map || !job?.fields?.length) return;

  const allPoints = job.fields.flatMap((field) => {
    const path = field?.polygonPath || field?.points || [];
    return Array.isArray(path) ? path.filter(isValidPoint) : [];
  });

  if (allPoints.length > 0) {
    fitMapToPath(map, allPoints, 70);
    return;
  }

  const firstField = job.fields[0];
  if (typeof firstField?.lat === "number" && typeof firstField?.lng === "number") {
    map.panTo({ lat: firstField.lat, lng: firstField.lng });
    map.setZoom(15);
  }
};

const fitMapToDayJob = (map, job) => {
  if (!map || typeof job?.lat !== "number" || typeof job?.lng !== "number") return;
  map.panTo({ lat: job.lat, lng: job.lng });
  map.setZoom(15);
};

const getJobTotalAcres = (job) =>
  Array.isArray(job?.fields)
    ? job.fields.reduce((sum, field) => sum + (Number(field.acres) || 0), 0)
    : 0;

const formatUnknownBoolean = (value) => {
  if (value === true || value === "true") return "Yes";
  if (value === false || value === "false") return "No";
  return "Unknown";
};

const MapView = ({
  isLoaded,
  loadError,
  userLocation,
  setUserLocation,
  completedFields = [],
  setCompletedFields = () => {},
  showExistingJobs = true,
  currentUser = null,
  focusJobId = null,
  showDrawingControls = true,

  entranceMode = false,
  setEntranceMode = () => {},
  selectedFieldIndexForEntrance = 0,
  setSelectedFieldIndexForEntrance = () => {},
  drawingContext = "field_job",

}) => {
  const [jobs, setJobs] = useState([]);
  const [dayJobs, setDayJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [landUsePosts, setLandUsePosts] = useState([]);
  const [farmStands, setFarmStands] = useState([]);

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedLandUse, setSelectedLandUse] = useState(null);
  const [selectedFarmStand, setSelectedFarmStand] = useState(null);
  const [hoveredJobId, setHoveredJobId] = useState(null);
  const [mapRef, setMapRef] = useState(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [draftPath, setDraftPath] = useState([]);
  const [draftAreaSqMeters, setDraftAreaSqMeters] = useState(0);
  const [draftAcres, setDraftAcres] = useState(0);
  const [draftCategory, setDraftCategory] = useState("hay");
  const [draftCity, setDraftCity] = useState("");
  const [draftState, setDraftState] = useState("");
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);

  const editablePolygonRef = useRef(null);
  const editablePolygonListenersRef = useRef([]);
  const hasCenteredOnUserRef = useRef(false);
  const mapShellRef = useRef(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [showDrawTools, setShowDrawTools] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const routeFocusJobId = location.state?.focusJobId || null;
  const routeFocusItemId = location.state?.focusItemId || null;
  const routeFocusItemType = location.state?.focusItemType || null;
  const effectiveFocusJobId = focusJobId || routeFocusJobId;
  const userLocationMarkerPosition = useMemo(
    () => getRenderableUserLocationMarkerPoint(userLocation),
    [userLocation]
  );
  const browsingMapCenter = useMemo(() => normalizeCoordinates(userLocation), [userLocation]);
  const mapCenter = browsingMapCenter || visualMapCenter;

  useEffect(() => {
    if (!showExistingJobs) {
      setJobs([]);
      setDayJobs([]);
      setLandUsePosts([]);
      setFarmStands([]);
      setSelectedJob(null);
      setSelectedEvent(null);
      setSelectedLandUse(null);
      setSelectedFarmStand(null);
      return;
    }

    const unsubscribeDayJobs = onSnapshot(collection(db, "dayJobs"), (snapshot) => {
      const docs = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            isDayJob: true,
            lat: typeof data.lat === "string" ? Number(data.lat) : data.lat,
            lng: typeof data.lng === "string" ? Number(data.lng) : data.lng,
          };
        })
        .filter((job) => !job.isArchived && job.status !== "completed");

      setDayJobs(docs);
    });

    const unsubscribeFields = onSnapshot(collection(db, "fields"), (snapshot) => {
      const docs = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();

          const normalizedFields = Array.isArray(data.fields)
            ? data.fields.map((field) => ({
                ...field,
                id:
                  field.id ||
                  `${docSnap.id}-${field.lat || "field"}-${field.lng || "field"}`,
                lat: typeof field.lat === "string" ? Number(field.lat) : field.lat,
                lng: typeof field.lng === "string" ? Number(field.lng) : field.lng,
                acres:
                  typeof field.acres === "string"
                    ? Number(field.acres)
                    : field.acres || 0,
                areaSqMeters:
                  typeof field.areaSqMeters === "string"
                    ? Number(field.areaSqMeters)
                    : field.areaSqMeters || 0,
                polygonPath: Array.isArray(field.polygonPath)
                  ? field.polygonPath.map(normalizePoint).filter(isValidPoint)
                  : Array.isArray(field.points)
                  ? field.points.map(normalizePoint).filter(isValidPoint)
                  : [],
              }))
            : [];

          return {
            id: docSnap.id,
            ...data,
            isDayJob: false,
            fields: normalizedFields,
          };
        })
        .filter((job) => !job.isArchived && job.status !== "completed");

      setJobs(docs);
    });

    const unsubscribeLandUse = onSnapshot(collection(db, "communityPosts"), (snapshot) => {
      const docs = snapshot.docs
        .map((docSnap) => {
          const data = docSnap.data();
          const rawFields = Array.isArray(data.fields)
            ? data.fields
            : Array.isArray(data.polygons)
            ? data.polygons
            : [];
          const normalizedFields = rawFields.map((field) => ({
                ...field,
                id:
                  field.id ||
                  `${docSnap.id}-${field.lat || "land"}-${field.lng || "land"}`,
                lat: typeof field.lat === "string" ? Number(field.lat) : field.lat,
                lng: typeof field.lng === "string" ? Number(field.lng) : field.lng,
                acres:
                  typeof field.acres === "string"
                    ? Number(field.acres)
                    : field.acres || 0,
                areaSqMeters:
                  typeof field.areaSqMeters === "string"
                    ? Number(field.areaSqMeters)
                    : field.areaSqMeters || 0,
                polygonPath: Array.isArray(field.polygonPath)
                  ? field.polygonPath.map(normalizePoint).filter(isValidPoint)
                  : Array.isArray(field.points)
                  ? field.points.map(normalizePoint).filter(isValidPoint)
                  : [],
              }));

          return {
            id: docSnap.id,
            ...data,
            isLandUse: true,
            fields: normalizedFields,
            lat: typeof data.lat === "string" ? Number(data.lat) : data.lat,
            lng: typeof data.lng === "string" ? Number(data.lng) : data.lng,
          };
        })
        .filter((post) => post.type === "land_use")
        .filter((post) => post.isArchived !== true && post.isHidden !== true)
        .filter((post) => (post.status || "active") === "active");

      setLandUsePosts(docs);
    });

    const unsubscribeFarmStands = onSnapshot(
      query(collection(db, "farmStands"), where("status", "in", ["active", "open"])),
      (snapshot) => {
        const docs = snapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();
            const stand = { id: docSnap.id, ...data };
            const mapPoint = getFarmStandMapPoint(stand);

            return {
              ...stand,
              mapPoint,
            };
          })
          .filter(isFarmStandMapEligible);

        setFarmStands(docs);
      }
    );

    return () => {
      unsubscribeDayJobs();
      unsubscribeFields();
      unsubscribeLandUse();
      unsubscribeFarmStands();
    };
  }, [showExistingJobs]);

  const isUpcomingVisibleEvent = (event) => {
  if (!event) return false;

  if (event.isArchived === true) return false;
  if (event.status === "expired") return false;

  // If an event has no date, keep it visible so new/unfinished posts don't vanish by accident.
  if (!event.eventDate) return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let eventDate;

  if (event.eventDate?.toDate) {
    eventDate = event.eventDate.toDate();
  } else {
    eventDate = new Date(event.eventDate);
  }

  if (Number.isNaN(eventDate.getTime())) return true;

  eventDate.setHours(0, 0, 0, 0);

  return eventDate >= today;
};

  useEffect(() => {
  const unsub = onSnapshot(collection(db, "communityEvents"), (snapshot) => {
    const docs = snapshot.docs
      .map((docSnap) => {
        const data = docSnap.data();

        return {
          id: docSnap.id,
          ...data,
          lat: typeof data.lat === "string" ? Number(data.lat) : data.lat,
          lng: typeof data.lng === "string" ? Number(data.lng) : data.lng,
        };
      })
      .filter(isUpcomingVisibleEvent);

    setEvents(docs);
  });

  return () => unsub();
}, []);

  useEffect(() => {
    if (!effectiveFocusJobId || !showExistingJobs || jobs.length === 0 || !mapRef) return;

    const targetJob = jobs.find((job) => job.id === effectiveFocusJobId);
    if (!targetJob || !Array.isArray(targetJob.fields) || targetJob.fields.length === 0) return;

    setSelectedEvent(null);
    setSelectedLandUse(null);
    setSelectedFarmStand(null);
    setSelectedJob(targetJob);
    fitMapToJob(mapRef, targetJob);
  }, [effectiveFocusJobId, showExistingJobs, jobs, mapRef]);

  useEffect(() => {
    if (
      !routeFocusItemId ||
      routeFocusItemType !== "event" ||
      !showExistingJobs ||
      !mapRef ||
      events.length === 0
    ) {
      return;
    }

    const targetEvent = events.find((event) => event.id === routeFocusItemId);

    if (!targetEvent) {
      return;
    }

    setSelectedJob(null);
    setSelectedLandUse(null);
    setSelectedFarmStand(null);

    let cancelled = false;
    const resolveEventLocation = async () => {
      const locationQuery = getEventLocationQuery(targetEvent);
      const geocoded = locationQuery
        ? await geocodeExactLocation(locationQuery)
        : null;
      if (cancelled) return;

      const resolvedPoint = geocoded ||
        (typeof targetEvent.lat === "number" && typeof targetEvent.lng === "number"
          ? { lat: targetEvent.lat, lng: targetEvent.lng }
          : null);
      if (!resolvedPoint) return;

      const resolvedEvent = { ...targetEvent, ...resolvedPoint };
      setSelectedEvent(resolvedEvent);
      mapRef.panTo({ lat: resolvedPoint.lat, lng: resolvedPoint.lng });
      mapRef.setZoom(14);
    };

    resolveEventLocation();
    return () => {
      cancelled = true;
    };
  }, [routeFocusItemId, routeFocusItemType, showExistingJobs, mapRef, events]);

  useEffect(() => {
    if (
      !routeFocusItemId ||
      routeFocusItemType !== "dayJob" ||
      !showExistingJobs ||
      !mapRef ||
      dayJobs.length === 0
    ) {
      return;
    }

    const targetJob = dayJobs.find((job) => job.id === routeFocusItemId);
    if (!targetJob) return;

    setSelectedEvent(null);
    setSelectedLandUse(null);
    setSelectedFarmStand(null);
    setSelectedJob(targetJob);
    fitMapToDayJob(mapRef, targetJob);
  }, [routeFocusItemId, routeFocusItemType, showExistingJobs, mapRef, dayJobs]);

  useEffect(() => {
    if (
      !routeFocusItemId ||
      routeFocusItemType !== "landUse" ||
      !showExistingJobs ||
      !mapRef ||
      landUsePosts.length === 0
    ) {
      return;
    }

    const targetPost = landUsePosts.find((post) => post.id === routeFocusItemId);
    if (!targetPost) return;

    setSelectedJob(null);
    setSelectedEvent(null);
    setSelectedFarmStand(null);
    setSelectedLandUse(targetPost);

    if (Array.isArray(targetPost.fields) && targetPost.fields.length > 0) {
      fitMapToJob(mapRef, targetPost);
      return;
    }

    if (typeof targetPost.lat === "number" && typeof targetPost.lng === "number") {
      mapRef.panTo({ lat: targetPost.lat, lng: targetPost.lng });
      mapRef.setZoom(14);
    }
  }, [routeFocusItemId, routeFocusItemType, showExistingJobs, mapRef, landUsePosts]);

  useEffect(() => {
    if (!selectedJob || !mapRef || !showExistingJobs) return;

    if (selectedJob.isDayJob) {
      fitMapToDayJob(mapRef, selectedJob);
    } else {
      fitMapToJob(mapRef, selectedJob);
    }
  }, [selectedJob, mapRef, showExistingJobs]);

  useEffect(() => {
    if (
      !mapRef ||
      !browsingMapCenter?.lat ||
      !browsingMapCenter?.lng ||
      (routeFocusItemType === "event" && routeFocusItemId)
    ) return;

    mapRef.panTo(browsingMapCenter);
    mapRef.setZoom(showDrawingControls ? 16 : 12);

    hasCenteredOnUserRef.current = true;
  }, [
    mapRef,
    browsingMapCenter?.lat,
    browsingMapCenter?.lng,
    showDrawingControls,
    routeFocusItemId,
    routeFocusItemType,
  ]);

  const detectLocationDetails = (lat, lng) => {
    return new Promise((resolve) => {
      if (!window.google?.maps) {
        resolve({ city: "", state: "" });
        return;
      }

      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          const components = results[0].address_components || [];

          const city =
            components.find((c) => c.types.includes("locality"))?.long_name || "";

          const state =
            components.find((c) => c.types.includes("administrative_area_level_1"))
              ?.short_name || "";

          resolve({ city, state });
        } else {
          console.error("Geocoder failed:", status);
          resolve({ city: "", state: "" });
        }
      });
    });
  };

  const updateDraftArea = (path) => {
    if (!window.google?.maps?.geometry || path.length < 3) {
      setDraftAreaSqMeters(0);
      setDraftAcres(0);
      return;
    }

    const googlePath = path.map(
      (point) => new window.google.maps.LatLng(point.lat, point.lng)
    );

    const areaSqMeters = window.google.maps.geometry.spherical.computeArea(googlePath);
    const acres = Number(sqMetersToAcres(areaSqMeters).toFixed(2));

    setDraftAreaSqMeters(areaSqMeters);
    setDraftAcres(acres);
  };

  const syncDraftFromEditablePolygon = () => {
    if (!editablePolygonRef.current) return;

    const nextPath = readPathFromPolygon(editablePolygonRef.current);
    setDraftPath(nextPath);
    updateDraftArea(nextPath);
  };

  const clearEditablePolygonListeners = () => {
    editablePolygonListenersRef.current.forEach((listener) => {
      if (listener?.remove) listener.remove();
    });
    editablePolygonListenersRef.current = [];
  };

  const handleEditablePolygonLoad = (polygon) => {
    editablePolygonRef.current = polygon;
    clearEditablePolygonListeners();

    const path = polygon.getPath();

    editablePolygonListenersRef.current = [
      path.addListener("insert_at", syncDraftFromEditablePolygon),
      path.addListener("set_at", syncDraftFromEditablePolygon),
      path.addListener("remove_at", syncDraftFromEditablePolygon),
      polygon.addListener("dragend", syncDraftFromEditablePolygon),
      polygon.addListener("mouseup", syncDraftFromEditablePolygon),
    ];
  };

  const handleEditablePolygonUnmount = () => {
    clearEditablePolygonListeners();
    editablePolygonRef.current = null;
  };

  const zoomToField = (field) => {
    if (!mapRef) return;

    const path = field?.polygonPath || field?.points || [];

    if (Array.isArray(path) && path.length > 0) {
      fitMapToPath(mapRef, path, 80);
      return;
    }

    if (typeof field?.lat === "number" && typeof field?.lng === "number") {
      mapRef.panTo({ lat: field.lat, lng: field.lng });
      mapRef.setZoom(15);
    }
  };

  useEffect(() => {
  const handleFullscreenChange = () => {
    setIsMapFullscreen(document.fullscreenElement === mapShellRef.current);
  };

  document.addEventListener("fullscreenchange", handleFullscreenChange);

  return () => {
    document.removeEventListener("fullscreenchange", handleFullscreenChange);
  };
}, []);

const toggleMapFullscreen = async () => {
  try {
    if (!document.fullscreenElement) {
      await mapShellRef.current?.requestFullscreen();
    } else {
      await document.exitFullscreen();
    }
  } catch (err) {
    console.error("Fullscreen failed:", err);
  }
};

  useEffect(() => {
    window.zoomToField = zoomToField;

    return () => {
      delete window.zoomToField;
    };
  }, [mapRef, completedFields]);

  const startDrawing = () => {
    setIsDrawing(true);
    setEditingFieldIndex(null);
    setDraftPath([]);
    setDraftAreaSqMeters(0);
    setDraftAcres(0);
    setDraftCity("");
    setDraftState("");
    setDraftCategory(drawingContext === "land_use" ? "land_use" : "hay");
    setSelectedJob(null);
    setSelectedEvent(null);
    setSelectedLandUse(null);
  };

  const finishDrawing = async () => {
    const finalPath =
      editablePolygonRef.current && draftPath.length >= 3
        ? readPathFromPolygon(editablePolygonRef.current)
        : draftPath;

    if (finalPath.length < 3) {
      alert("Please add at least 3 points to make a field boundary.");
      return;
    }

    const markerPosition = finalPath[0];
    const locationDetails = await detectLocationDetails(markerPosition.lat, markerPosition.lng);

    let areaSqMeters = draftAreaSqMeters;
    let acres = draftAcres;

    if (window.google?.maps?.geometry) {
      const googlePath = finalPath.map(
        (point) => new window.google.maps.LatLng(point.lat, point.lng)
      );
      areaSqMeters = window.google.maps.geometry.spherical.computeArea(googlePath);
      acres = Number(sqMetersToAcres(areaSqMeters).toFixed(2));
    }

    const updatedField = {
      id:
        editingFieldIndex !== null && completedFields[editingFieldIndex]?.id
          ? completedFields[editingFieldIndex].id
          : `field-${Date.now()}`,
      acres,
      areaSqMeters,
      lat: markerPosition.lat,
      lng: markerPosition.lng,
      polygonPath: finalPath,
      points: finalPath,
      category: drawingContext === "land_use" ? "land_use" : draftCategory,
      city: locationDetails.city,
      state: locationDetails.state,
    };

    if (editingFieldIndex !== null) {
      setCompletedFields((prev) =>
        prev.map((field, index) => (index === editingFieldIndex ? updatedField : field))
      );
    } else {
      setCompletedFields((prev) => [...prev, updatedField]);
    }

    setDraftCity(locationDetails.city);
    setDraftState(locationDetails.state);
    setDraftPath([]);
    setDraftAreaSqMeters(0);
    setDraftAcres(0);
    setDraftCategory(drawingContext === "land_use" ? "land_use" : "hay");
    setEditingFieldIndex(null);
    setIsDrawing(false);
  };

  const cancelDraft = () => {
    setIsDrawing(false);
    setEditingFieldIndex(null);
    setDraftPath([]);
    setDraftAreaSqMeters(0);
    setDraftAcres(0);
    setDraftCity("");
    setDraftState("");
    setDraftCategory(drawingContext === "land_use" ? "land_use" : "hay");
  };

  const undoLastPoint = () => {
    if (editablePolygonRef.current) {
      const path = editablePolygonRef.current.getPath();
      if (path.getLength() > 0) path.removeAt(path.getLength() - 1);
      return;
    }

    if (draftPath.length === 0) return;

    const nextPath = draftPath.slice(0, -1);
    setDraftPath(nextPath);
    updateDraftArea(nextPath);
  };

  const removeCompletedField = (indexToRemove) => {
    setCompletedFields((prev) => prev.filter((_, index) => index !== indexToRemove));

    if (editingFieldIndex === indexToRemove) cancelDraft();
  };

  const editCompletedField = (indexToEdit) => {
    const fieldToEdit = completedFields[indexToEdit];
    if (!fieldToEdit) return;

    const pathToEdit = fieldToEdit.polygonPath || fieldToEdit.points || [];

    setDraftPath(pathToEdit);
    setDraftAreaSqMeters(fieldToEdit.areaSqMeters || 0);
    setDraftAcres(fieldToEdit.acres || 0);
    setDraftCategory(fieldToEdit.category || "hay");
    setDraftCity(fieldToEdit.city || "");
    setDraftState(fieldToEdit.state || "");
    setEditingFieldIndex(indexToEdit);
    setIsDrawing(true);

    if (fieldToEdit.lat && fieldToEdit.lng) {
      const nextLocation = { lat: fieldToEdit.lat, lng: fieldToEdit.lng };
      setUserLocation(nextLocation);
      zoomToField(fieldToEdit);
    }
  };

const placeEntranceAt = (lat, lng) => {
  if (!completedFields[selectedFieldIndexForEntrance]) {
    alert("Choose a saved field before setting an entrance.");
    setEntranceMode(false);
    return;
  }

  setCompletedFields((prev) =>
    prev.map((field, index) =>
      index === selectedFieldIndexForEntrance
        ? {
            ...field,
            entrance: {
              ...(field.entrance || {}),
              lat,
              lng,
            },
            entranceLat: lat,
            entranceLng: lng,
          }
        : field
    )
  );

  setEntranceMode(false);
};

  const handleMapClick = (e) => {
    if (!e.latLng) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

   if (entranceMode) {
  placeEntranceAt(lat, lng);
  return;
}

  

    if (isDrawing) {
      const nextPath = [...draftPath, { lat, lng }];
      setDraftPath(nextPath);
      updateDraftArea(nextPath);

      if (nextPath.length >= 2 && mapRef) fitMapToPath(mapRef, nextPath, 80);
    }
  };

  const draftMarkerPosition = useMemo(() => {
    if (draftPath.length === 0) return null;
    return draftPath[0];
  }, [draftPath]);

  const getMarkerIcon = (type, isActive = false) => {
    if (!window.google?.maps?.Size) return undefined;

    return {
      url: createFarmMapIcon(type, isActive),
      scaledSize: new window.google.maps.Size(isActive ? 44 : 38, isActive ? 44 : 38),
    };
  };

  const getEntranceMarkerIcon = (isActive = false) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
      <g fill="none" stroke="${isActive ? "#14532d" : "#234d20"}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
        <!-- left post -->
        <path d="M10 38V10" />
        <path d="M8 10h4" />
        <!-- right post -->
        <path d="M38 38V10" />
        <path d="M36 10h4" />
        <!-- open gate panels -->
        <path d="M12 36V14l10 4v14z" />
        <path d="M36 36V14l-10 4v14z" />
        <!-- hinge / latch -->
        <path d="M22 24h4" />
        <!-- ground -->
        <path d="M6 38h36" />
      </g>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(isActive ? 30 : 26, isActive ? 30 : 26),
    anchor: new window.google.maps.Point(isActive ? 15 : 13, isActive ? 15 : 13),
  };
};

  const FarmMapBadge = ({ type }) => {
    const meta = getFarmIconMeta(type);

    return (
      <span
        className="map-job-badge"
        style={{
          background: meta.bg,
          color: meta.color,
          borderColor: `${meta.color}33`,
        }}
      >
        <span>{meta.emoji}</span>
        {meta.label}
      </span>
    );
  };

  const FarmMapPopupCard = ({ children }) => (
    <div
      className="farm-map-popup-card"
      style={{
        width: "250px",
        lineHeight: 1.45,
        fontFamily: "sans-serif",
      }}
    >
      {children}
    </div>
  );

  const StatusText = ({ status }) => (
    <p style={{ margin: "6px 0" }}>
      Status:{" "}
      <span
        style={{
          color:
            status === "completed"
              ? "#2563eb"
              : status === "in_progress"
              ? "#f59e0b"
              : "#16a34a",
          fontWeight: "800",
        }}
      >
        {status || "open"}
      </span>
    </p>
  );

  const totalDraftedAcres = completedFields
    .reduce((sum, field) => sum + (Number(field.acres) || 0), 0)
    .toFixed(2);

  const totalVisibleJobs = jobs.length + dayJobs.length + landUsePosts.length;
  const totalVisibleFields = jobs.reduce(
    (sum, job) => sum + (Array.isArray(job.fields) ? job.fields.length : 0),
    0
  );
  const isLandUseDrawing = drawingContext === "land_use";
  const drawingTerm = isLandUseDrawing ? "land area" : "field";
  const drawingTermTitle = isLandUseDrawing ? "Land Areas" : "Fields";

  const mapOptions = useMemo(() => {
    if (!window.google?.maps) return undefined;

    return {
      mapTypeId: "hybrid",
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.TOP_LEFT,
      },
      streetViewControl: true,
      fullscreenControl: false,
      zoomControl: true,
      clickableIcons: false,
      gestureHandling: "greedy",
      styles: [
        { featureType: "poi.business", stylers: [{ visibility: "off" }] },
        { featureType: "landscape", stylers: [{ color: "#f4f7f3" }] },
        { featureType: "water", stylers: [{ color: "#a5d6ff" }] },
      ],
    };
  }, []);

  const getJobCollection = (job) => (job?.isDayJob === true ? "dayJobs" : "fields");
  
  

  const acceptJob = async (job) => {
    if (!currentUser || job.ownerId === currentUser.uid) return;

    const userSnap = await getDoc(doc(db, "users", currentUser.uid));
    const userData = userSnap.data() || {};
    const collectionName = getJobCollection(job);

    await updateDoc(doc(db, collectionName, job.id), {
      status: "in_progress",
      acceptedBy: currentUser.uid,
      acceptedByName: userData.name || "",
      acceptedByPhoto: userData.profilePicture || "",
      acceptedAt: new Date(),
    });
  };

  const completeJob = async (job) => {
    if (!job?.acceptedBy) return;

    const collectionName = getJobCollection(job);

    await updateDoc(doc(db, collectionName, job.id), {
      status: "completed",
      completedBy: job.acceptedBy,
      completedByName: job.acceptedByName || "",
      completedByPhoto: job.acceptedByPhoto || "",
      completedAt: new Date(),
    });

    setSelectedJob(null);
    alert("Job marked completed.");
  };

  const getSelectedDirectionsDestination = () => {
  if (selectedFarmStand?.mapPoint?.lat && selectedFarmStand?.mapPoint?.lng) {
    return `${selectedFarmStand.mapPoint.lat},${selectedFarmStand.mapPoint.lng}`;
  }

  if (selectedJob?.selectedEntrance?.lat && selectedJob?.selectedEntrance?.lng) {
    return `${selectedJob.selectedEntrance.lat},${selectedJob.selectedEntrance.lng}`;
  }

  if (selectedEvent?.lat && selectedEvent?.lng) {
    return `${selectedEvent.lat},${selectedEvent.lng}`;
  }

  if (selectedLandUse?.selectedEntrance?.lat && selectedLandUse?.selectedEntrance?.lng) {
    return `${selectedLandUse.selectedEntrance.lat},${selectedLandUse.selectedEntrance.lng}`;
  }

  const firstLandUseField = selectedLandUse?.fields?.[0];
  const landUseEntranceLat =
    firstLandUseField?.entrance?.lat || firstLandUseField?.entranceLat;
  const landUseEntranceLng =
    firstLandUseField?.entrance?.lng || firstLandUseField?.entranceLng;

  if (landUseEntranceLat && landUseEntranceLng) {
    return `${landUseEntranceLat},${landUseEntranceLng}`;
  }

  if (firstLandUseField?.lat && firstLandUseField?.lng) {
    return `${firstLandUseField.lat},${firstLandUseField.lng}`;
  }

  if (selectedLandUse?.lat && selectedLandUse?.lng) {
    return `${selectedLandUse.lat},${selectedLandUse.lng}`;
  }

  if (selectedJob?.isDayJob && selectedJob?.lat && selectedJob?.lng) {
    return `${selectedJob.lat},${selectedJob.lng}`;
  }

  const firstField = selectedJob?.fields?.[0];

  const entranceLat = firstField?.entrance?.lat || firstField?.entranceLat;
  const entranceLng = firstField?.entrance?.lng || firstField?.entranceLng;
const entranceNote =
    selectedJob?.selectedEntrance?.note ||
    firstField?.entrance?.note ||
    firstField?.entranceNote ||
    "";
  if (entranceLat && entranceLng) {
    return `${entranceLat},${entranceLng}`;
  }

  if (firstField?.lat && firstField?.lng) {
    return `${firstField.lat},${firstField.lng}`;
  }

  return null;
};

  const openSelectedDirections = () => {
    const destination = getSelectedDirectionsDestination();

    if (!destination) {
      alert("No location available for directions.");
      return;
    }

    window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination}`, "_blank");
  };

  const selectedLandUsePosition = (() => {
    const entrance = selectedLandUse?.selectedEntrance;
    if (entrance?.lat && entrance?.lng) {
      return { lat: entrance.lat, lng: entrance.lng };
    }

    const firstField = selectedLandUse?.fields?.[0];
    if (typeof firstField?.lat === "number" && typeof firstField?.lng === "number") {
      return { lat: firstField.lat, lng: firstField.lng };
    }

    if (typeof selectedLandUse?.lat === "number" && typeof selectedLandUse?.lng === "number") {
      return { lat: selectedLandUse.lat, lng: selectedLandUse.lng };
    }

    return null;
  })();

  if (loadError) {
    return (
      <div className="card">
        The map could not load right now. Please refresh the page or try again in a moment.
      </div>
    );
  }

  if (!isLoaded) {
    return <div className="card">Loading Map...</div>;
  }

  return (
    <div className="card section map-card">
      {showDrawingControls && (
        <>
          <div className="map-header-row">
            <div>
              <h2 style={{ marginBottom: "6px" }}>Map</h2>
              <p className="map-subtitle" style={{ margin: 0 }}>
                {isLandUseDrawing
                  ? "Draw the pasture, garden plot, fenced area, or field access area."
                  : "Browse nearby listings, focus a job, or draw a field boundary with smoother controls."}
              </p>
            </div>

            <div className="map-stat-pills">
              {showExistingJobs ? (
                <>
                  <span className="map-stat-pill">
                    Jobs: <strong>{totalVisibleJobs}</strong>
                  </span>
                  <span className="map-stat-pill">
                    Field areas: <strong>{totalVisibleFields}</strong>
                  </span>
                </>
              ) : (
                <>
                  <span className="map-stat-pill">
                    Draft {drawingTermTitle.toLowerCase()}: <strong>{completedFields.length}</strong>
                  </span>
                  <span className="map-stat-pill">
                    Acres: <strong>{totalDraftedAcres}</strong>
                  </span>
                </>
              )}
            </div>
          </div>

          <div className={`info-panel${isLandUseDrawing ? " land-use-draw-callout" : ""}`}>
            <div className="info-panel-title">How it works</div>
            <div className="info-panel-text">
              <div>
                1. Click <strong>Add {isLandUseDrawing ? "Land Area" : "Field"}</strong>
              </div>
              <div>2. Click around the {drawingTerm} to outline it</div>
              <div>3. Drag corners or the whole shape to fine-tune it</div>
              <div>
                4. Click <strong>Save {isLandUseDrawing ? "Land Area" : "Field"}</strong>
              </div>
            </div>
          </div>

          
        </>
      )}

      {completedFields.length > 0 && (
        <div style={{ marginBottom: "12px" }}>
          <strong>{isLandUseDrawing ? "Land areas in this post:" : "Fields in this job:"}</strong>
          <div className="field-list" style={{ marginTop: "8px" }}>
            {completedFields.map((field, index) => {
              const isEditing = editingFieldIndex === index;

              return (
                <div
                  key={field.id || `field-summary-${index}`}
                  className={`field-card ${isEditing ? "active" : ""}`}
                  onClick={() => zoomToField(field)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="field-card-header">
                    <span>
                      <strong className="field-card-title">
                        {isLandUseDrawing ? "Land Area" : "Field"} {index + 1}
                      </strong>:{" "}
                      {field.acres} acres, {field.category}
                      {field.city || field.state
                        ? `, ${field.city}${field.city && field.state ? ", " : ""}${field.state}`
                        : ""}
                    </span>
                  </div>

                  <div className="field-card-actions">
                    <button
                      type="button"
                      className="btn-soft"
                      onClick={(e) => {
                        e.stopPropagation();
                        zoomToField(field);
                      }}
                    >
                      Zoom to {isLandUseDrawing ? "Land" : "Field"}
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        editCompletedField(index);
                      }}
                    >
                      Edit {isLandUseDrawing ? "Land Area" : "Field"}
                    </button>

                    <button
                      type="button"
                      className="btn-danger-soft"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCompletedField(index);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div
  ref={mapShellRef}
  className={`map-shell field-drawing-map-shell ${
    isMapFullscreen ? "is-map-fullscreen" : ""
  }`}
  style={{ position: "relative" }}
>
  {showDrawingControls && !showExistingJobs && (
    <div
      className={`field-drawing-overlay${showDrawTools ? " is-open" : ""}${
        isDrawing ? " is-drawing" : ""
      }`}
    >
      <div className="field-drawing-toolbar compact">
  <button
    type="button"
    className="field-drawing-menu-btn"
    onClick={() => setShowDrawTools((prev) => !prev)}
  >
    Draw Tools
    <span>{showDrawTools ? "Close" : "Open"}</span>
  </button>

  <button
    type="button"
    className="field-drawing-fullscreen-btn"
    onClick={toggleMapFullscreen}
    title={isMapFullscreen ? "Exit fullscreen" : "Fullscreen"}
  >
    {isMapFullscreen ? "Exit" : "Full"}
  </button>
</div>

{showDrawTools && (
  <div className="field-drawing-dropdown">
    <button
      type="button"
      onClick={() => {
        startDrawing();
        setShowDrawTools(false);
      }}
    >
      Add {isLandUseDrawing ? "Land Area" : "Field"}
    </button>

    <button
      type="button"
      className="btn-primary"
      onClick={() => {
        finishDrawing();
        setShowDrawTools(false);
      }}
      disabled={!isDrawing || draftPath.length < 3}
    >
      {editingFieldIndex !== null
        ? "Save Changes"
        : `Save ${isLandUseDrawing ? "Land Area" : "Field"}`}
    </button>

    <button
      type="button"
      onClick={undoLastPoint}
      disabled={draftPath.length === 0}
    >
      Undo Last Point
    </button>

    <button
      type="button"
      className="btn-danger-soft"
      onClick={() => {
        cancelDraft();
        setShowDrawTools(false);
      }}
      disabled={draftPath.length === 0 && !isDrawing}
    >
      Cancel
    </button>
    {completedFields.length > 0 && (
  <div className="draw-tool-divider">
    <strong>Entrance</strong>

    {completedFields.length > 1 && (
      <select
        value={selectedFieldIndexForEntrance}
        onChange={(e) =>
          setSelectedFieldIndexForEntrance(Number(e.target.value))
        }
      >
        {completedFields.map((field, index) => (
          <option key={field.id || index} value={index}>
            Field #{index + 1}
          </option>
        ))}
      </select>
    )}

    <button
      type="button"
      className={entranceMode ? "btn-primary" : ""}
      onClick={() => {
        setEntranceMode(true);
        setShowDrawTools(false);
      }}
    >
      {entranceMode ? "Click map to place entrance" : "Set Entrance"}
    </button>

    {(completedFields[selectedFieldIndexForEntrance]?.entrance?.lat ||
      completedFields[selectedFieldIndexForEntrance]?.entranceLat) && (
      <button
        type="button"
        className="btn-danger-soft"
        onClick={() => {
          setCompletedFields((prev) =>
            prev.map((field, index) =>
              index === selectedFieldIndexForEntrance
                ? {
                    ...field,
                    entrance: null,
                    entranceLat: null,
                    entranceLng: null,
                    entranceNote: "",
                  }
                : field
            )
          );
        }}
      >
        Remove Entrance
      </button>
    )}
  </div>
)}
  </div>
)}

      <div className="field-drawing-status">
        {isDrawing ? (
          <strong>
            {editingFieldIndex !== null
              ? `Editing ${isLandUseDrawing ? "Land Area" : "Field"} ${editingFieldIndex + 1}`
              : `Click around the ${drawingTerm} boundary`}
          </strong>
        ) : (
          <strong>
            Ready to add another {drawingTerm} whenever you are.
          </strong>
        )}

        <span>
          Current {drawingTerm} size: <strong>{Number(draftAcres || 0).toFixed(2)} acres</strong>
        </span>

        {!showExistingJobs && (
          <span>
            {drawingTermTitle} added: <strong>{completedFields.length}</strong> | Total acres:{" "}
            <strong>{totalDraftedAcres}</strong>
          </span>
        )}

        {entranceMode && (
  <span>
    <strong>Entrance mode active:</strong> click the gate, driveway, or best pull-in spot.
  </span>
)}

        {(draftCity || draftState) && (
          <span>
            Location:{" "}
            <strong>
              {draftCity}
              {draftCity && draftState ? ", " : ""}
              {draftState}
            </strong>
          </span>
        )}
      </div>
    </div>
  )}

  {(selectedJob || selectedEvent || selectedLandUse || selectedFarmStand) && !isDrawing && (
          <button
            type="button"
            className="map-floating-nav-btn"
            style={{ zIndex: 9999 }}
            onClick={openSelectedDirections}
          >
            Navigate
          </button>
        )}

        <GoogleMap
           mapContainerStyle={{
           ...containerStyle,
           height: isMapFullscreen ? "100vh" : "560px",
             borderRadius: isMapFullscreen ? "0" : "18px",
              }}
          center={mapCenter}
          zoom={10}
          options={mapOptions}
          onLoad={(map) => setMapRef(map)}
          onClick={handleMapClick}
        >
          {showExistingJobs &&
            jobs.map((job) =>
              Array.isArray(job.fields)
                ? job.fields.map((field, index) => {
                    const isFocused = job.id === effectiveFocusJobId;
                    const isSelected = selectedJob?.id === job.id && !selectedJob?.isDayJob;
                    const isHovered = hoveredJobId === job.id;
                    const isActive = isFocused || isSelected || isHovered;

                    return (
                      <React.Fragment key={`${job.id}-field-${index}`}>
                        {Array.isArray(field.polygonPath) && field.polygonPath.length >= 3 && (
                          <Polygon
                            path={field.polygonPath}
                            options={{
                              fillColor: isFocused
                                ? "#2563eb"
                                : isSelected
                                ? "#3b82f6"
                                : isHovered
                                ? "#7cb342"
                                : "#8bc34a",
                              fillOpacity: isFocused ? 0.28 : isSelected ? 0.22 : isHovered ? 0.18 : 0.12,
                              strokeColor: isFocused
                                ? "#1d4ed8"
                                : isSelected
                                ? "#2563eb"
                                : isHovered
                                ? "#558b2f"
                                : "#689f38",
                              strokeOpacity: 1,
                              strokeWeight: isActive ? 4 : 2,
                              zIndex: isActive ? 50 : 5,
                            }}
                            onMouseOver={() => setHoveredJobId(job.id)}
                            onMouseOut={() => setHoveredJobId(null)}
                            onClick={() => {
                              setSelectedEvent(null);
                              setSelectedLandUse(null);
                              setSelectedFarmStand(null);
                              setSelectedJob(job);
                            }}
                          />
                        )}

                        {typeof field.lat === "number" && typeof field.lng === "number" && (
                          <Marker
                            position={{ lat: field.lat, lng: field.lng }}
                            icon={getMarkerIcon(field.category || job.grassType || "hay", isActive)}
                            onMouseOver={() => setHoveredJobId(job.id)}
                            onMouseOut={() => setHoveredJobId(null)}
                            onClick={() => {
                              setSelectedEvent(null);
                              setSelectedLandUse(null);
                              setSelectedFarmStand(null);
                              setSelectedJob(job);
                            }}
                            zIndex={isActive ? 60 : 6}
                          />
                          )}
{(field.entrance?.lat || field.entranceLat) &&
  (field.entrance?.lng || field.entranceLng) && (
    <Marker
  position={{
    lat: field.entrance?.lat || field.entranceLat,
    lng: field.entrance?.lng || field.entranceLng,
  }}
  icon={getEntranceMarkerIcon(isActive)}
  title={`Entrance for ${job.grassType || "Field Job"} Field #${index + 1}`}
  onClick={() => {
    setSelectedEvent(null);
    setSelectedLandUse(null);
    setSelectedFarmStand(null);
    setSelectedJob({
      ...job,
      selectedEntrance: {
        fieldIndex: index,
        lat: field.entrance?.lat || field.entranceLat,
        lng: field.entrance?.lng || field.entranceLng,
        note: field.entrance?.note || field.entranceNote || "",
      },
    });
  }}
  zIndex={isActive ? 90 : 75}
/>
  )}

                        
                      </React.Fragment>
                    );
                  })
                : null
            )}

          {showExistingJobs &&
            dayJobs.map((job) => {
              if (typeof job?.lat !== "number" || typeof job?.lng !== "number") return null;

              const isSelected = selectedJob?.id === job.id && selectedJob?.isDayJob;

              return (
                <Marker
                  key={`day-${job.id}`}
                  position={{ lat: job.lat, lng: job.lng }}
                  icon={getMarkerIcon(job.category || "dayJob", isSelected)}
                  onClick={() => {
                    setSelectedEvent(null);
                    setSelectedLandUse(null);
                    setSelectedFarmStand(null);
                    setSelectedJob(job);
                  }}
                  zIndex={isSelected ? 80 : 70}
                />
              );
            })}

          {showExistingJobs &&
            events.map((event) => {
              if (typeof event.lat !== "number" || typeof event.lng !== "number") return null;

              const isSelected = selectedEvent?.id === event.id;

              return (
                <Marker
                  key={`event-${event.id}`}
                  position={{ lat: event.lat, lng: event.lng }}
                  icon={getMarkerIcon(event.category || "event", isSelected)}
                  onClick={() => {
                    setSelectedJob(null);
                    setSelectedLandUse(null);
                    setSelectedFarmStand(null);
                    setSelectedEvent(event);
                  }}
                  zIndex={isSelected ? 100 : 90}
                />
              );
            })}

          {showExistingJobs &&
            farmStands.map((stand) => {
              const isSelected = selectedFarmStand?.id === stand.id;
              const position = stand.mapPoint;
              if (!position) return null;

              return (
                <Marker
                  key={`farm-stand-${stand.id}`}
                  position={position}
                  icon={getMarkerIcon(getFarmStandMapType(stand), isSelected)}
                  onClick={() => {
                    setSelectedJob(null);
                    setSelectedEvent(null);
                    setSelectedLandUse(null);
                    setSelectedFarmStand(stand);
                  }}
                  zIndex={isSelected ? 105 : 88}
                />
              );
            })}

          {showExistingJobs &&
            landUsePosts.map((post) => {
              const isSelected = selectedLandUse?.id === post.id;
              const fields = Array.isArray(post.fields) ? post.fields : [];

              if (fields.length === 0) {
                if (typeof post.lat !== "number" || typeof post.lng !== "number") return null;

                return (
                  <Marker
                    key={`land-use-${post.id}`}
                    position={{ lat: post.lat, lng: post.lng }}
                    icon={getMarkerIcon("land_use", isSelected)}
                    onClick={() => {
                      setSelectedJob(null);
                      setSelectedEvent(null);
                      setSelectedFarmStand(null);
                      setSelectedLandUse(post);
                    }}
                    zIndex={isSelected ? 110 : 72}
                  />
                );
              }

              return fields.map((field, index) => {
                const path = field.polygonPath || field.points || [];

                return (
                  <React.Fragment key={`land-use-${post.id}-field-${index}`}>
                    {Array.isArray(path) && path.length >= 3 && (
                      <Polygon
                        path={path}
                        options={{
                          fillColor: isSelected ? "#d97706" : "#f4b942",
                          fillOpacity: isSelected ? 0.24 : 0.16,
                          strokeColor: isSelected ? "#92400e" : "#b7791f",
                          strokeOpacity: 1,
                          strokeWeight: isSelected ? 4 : 2,
                          zIndex: isSelected ? 65 : 12,
                        }}
                        onClick={() => {
                          setSelectedJob(null);
                          setSelectedEvent(null);
                          setSelectedFarmStand(null);
                          setSelectedLandUse(post);
                        }}
                      />
                    )}

                    {typeof field.lat === "number" && typeof field.lng === "number" && (
                      <Marker
                        position={{ lat: field.lat, lng: field.lng }}
                        icon={getMarkerIcon("land_use", isSelected)}
                        onClick={() => {
                          setSelectedJob(null);
                          setSelectedEvent(null);
                          setSelectedFarmStand(null);
                          setSelectedLandUse(post);
                        }}
                        zIndex={isSelected ? 112 : 74}
                      />
                    )}

                    {(field.entrance?.lat || field.entranceLat) &&
                      (field.entrance?.lng || field.entranceLng) && (
                        <Marker
                          position={{
                            lat: field.entrance?.lat || field.entranceLat,
                            lng: field.entrance?.lng || field.entranceLng,
                          }}
                          icon={getEntranceMarkerIcon(isSelected)}
                          title={`Entrance for Land Share Area #${index + 1}`}
                          onClick={() => {
                            setSelectedJob(null);
                            setSelectedEvent(null);
                            setSelectedFarmStand(null);
                            setSelectedLandUse({
                              ...post,
                              selectedEntrance: {
                                fieldIndex: index,
                                lat: field.entrance?.lat || field.entranceLat,
                                lng: field.entrance?.lng || field.entranceLng,
                                note: field.entrance?.note || field.entranceNote || "",
                              },
                            });
                          }}
                          zIndex={isSelected ? 114 : 76}
                        />
                      )}
                  </React.Fragment>
                );
              });
            })}

          {showExistingJobs && selectedJob && (
            <InfoWindow
              position={
                selectedJob.isDayJob
                  ? { lat: selectedJob.lat, lng: selectedJob.lng }
                  : selectedJob.fields?.[0]
                  ? { lat: selectedJob.fields[0].lat, lng: selectedJob.fields[0].lng }
                  : undefined
              }
              onCloseClick={() => setSelectedJob(null)}
            >
              <FarmMapPopupCard>
                <div style={{ marginBottom: "8px" }}>
                  <FarmMapBadge
                    type={
                      selectedJob.isDayJob
                        ? selectedJob.category || "dayJob"
                        : selectedJob.grassType || selectedJob.fields?.[0]?.category || "hay"
                    }
                  />
                </div>

                <hr style={{ border: "none", borderTop: "1px solid #eee", margin: "8px 0" }} />

                {selectedJob.isDayJob ? (
                  <>
                    <div style={{ fontSize: "16px", fontWeight: "800", marginBottom: "4px" }}>
                      {getFarmIconMeta(selectedJob.category || "dayJob").emoji}{" "}
                      {selectedJob.title || "Day Job"}
                    </div>

                    {selectedJob.description && (
                      <p style={{ margin: "5px 0", color: "#4b5563" }}>
                        {selectedJob.description}
                      </p>
                    )}

                    <p style={{ margin: "5px 0" }}>
                      Category: <strong>{selectedJob.category || "general_labor"}</strong>
                    </p>

                    {selectedJob.payAmount && (
                      <p style={{ margin: "5px 0" }}>
                        Pay: <strong>${selectedJob.payAmount}</strong>
                      </p>
                    )}

                    {selectedJob.duration && (
                      <p style={{ margin: "5px 0" }}>
                        Duration: <strong>{selectedJob.duration}</strong>
                      </p>
                    )}

                    {selectedJob.dateNeeded && (
                      <p style={{ margin: "5px 0" }}>
                        Needed: <strong>{selectedJob.dateNeeded}</strong>
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: "16px", fontWeight: "800", marginBottom: "4px" }}>
                      {getJobTotalAcres(selectedJob).toFixed(2)} acre field job
                    </div>

                    <p style={{ margin: "5px 0" }}>
                      {selectedJob.city ||
                        selectedJob.fields?.[0]?.city ||
                        selectedJob.locationText ||
                        "Location available"}
                      {(selectedJob.state || selectedJob.fields?.[0]?.state) &&
                        `, ${selectedJob.state || selectedJob.fields?.[0]?.state}`}
                    </p>

                    <p style={{ margin: "5px 0" }}>
                      Acres: <strong>{getJobTotalAcres(selectedJob).toFixed(2)}</strong>
                    </p>

                    <p style={{ margin: "5px 0" }}>
                      Crop:{" "}
                      <strong>
                        {selectedJob.grassType ||
                          selectedJob.fields?.[0]?.category ||
                          "Field work"}
                      </strong>
                    </p>

                    
                  </>
                )}

                {selectedJob?.selectedEntrance && (
  <div className="entrance-info-box">
    <strong>Field Entrance</strong>

    <p>Field #{selectedJob.selectedEntrance.fieldIndex + 1}</p>

    {selectedJob.selectedEntrance.note && (
      <p>{selectedJob.selectedEntrance.note}</p>
    )}

    <button
      type="button"
      className="btn-soft"
      onClick={() => {
        const { lat, lng } = selectedJob.selectedEntrance;

        window.open(
          `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
          "_blank"
        );
      }}
    >
      Directions to Entrance
    </button>
  </div>
)}

                <StatusText status={selectedJob.status} />

                {selectedJob.acceptedByName && selectedJob.status === "in_progress" && (
                  <p style={{ margin: "5px 0" }}>{selectedJob.acceptedByName}</p>
                )}

                {selectedJob.completedByName && selectedJob.status === "completed" && (
                  <p style={{ margin: "5px 0" }}>✅ {selectedJob.completedByName}</p>
                )}

                <div style={{ marginTop: "10px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {selectedJob.status === "open" &&
  selectedJob.ownerId !== currentUser?.uid && (
    <button
      className="btn-primary"
      type="button"
      onClick={() => {
        navigate("/home", {
          state: {
            focusJobId: selectedJob.id,
            focusItemId: selectedJob.id,
            focusItemType: selectedJob.isDayJob ? "dayJob" : "fieldJob",
            dashboardView: "worker",
            radius: 100,
          },
        });
      }}
    >

      {selectedJob.isDayJob ? "Apply from Job Feed" : "Bid from Job Feed"}
    </button>
  )}

                  {!selectedJob.isDayJob &&
                    selectedJob.status === "in_progress" &&
                    (selectedJob.ownerId === currentUser?.uid ||
                      selectedJob.acceptedBy === currentUser?.uid) && (
                      <button type="button" onClick={() => completeJob(selectedJob)}>
                        Complete
                      </button>
                    )}

                  {selectedJob.status === "open" && selectedJob.ownerId === currentUser?.uid && (
                    <button
                      type="button"
                      className="btn-danger-soft"
                      onClick={async () => {
                        const confirmDelete = window.confirm(
                          "Delete this job? This cannot be undone."
                        );
                        if (!confirmDelete) return;

                        const collectionName = selectedJob.isDayJob ? "dayJobs" : "fields";

                        await updateDoc(doc(db, collectionName, selectedJob.id), {
                          isArchived: true,
                        });

                        setSelectedJob(null);
                      }}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </FarmMapPopupCard>
            </InfoWindow>
          )}

          {showExistingJobs && selectedEvent && (
            <InfoWindow
              position={{ lat: selectedEvent.lat, lng: selectedEvent.lng }}
              onCloseClick={() => setSelectedEvent(null)}
            >
              {(() => {
                const eventDetails = getEventMapPopupDetails(selectedEvent);
                return (
              <FarmMapPopupCard>
                {(selectedEvent.image || selectedEvent.imageUrl) && (
                  <img
                    src={selectedEvent.image || selectedEvent.imageUrl}
                    alt={selectedEvent.title || "Event"}
                    style={{
                      width: "100%",
                      height: "120px",
                      objectFit: "cover",
                      borderRadius: "10px",
                      marginBottom: "8px",
                    }}
                  />
                )}

                <div style={{ marginBottom: "8px" }}>
                  <FarmMapBadge type="event" />
                </div>

                <div style={{ fontSize: "16px", fontWeight: "800", marginBottom: "4px" }}>
                  {eventDetails.title}
                </div>

                {eventDetails.organizer && (
                  <p style={{ margin: "5px 0" }}>
                    Organizer: <strong>{eventDetails.organizer}</strong>
                  </p>
                )}

                <p style={{ margin: "5px 0" }}>
                  {eventDetails.location}
                </p>

                {selectedEvent.eventDate && (
                  <p style={{ margin: "5px 0" }}>
                    <strong>{eventDetails.date}</strong>
                  </p>
                )}

                {eventDetails.time && (
                  <p style={{ margin: "5px 0" }}>{eventDetails.time}</p>
                )}

                {eventDetails.description && (
                  <p style={{ margin: "6px 0", color: "#4b5563" }}>
                    {eventDetails.description}
                  </p>
                )}

                {eventDetails.category && (
                  <p style={{ margin: "5px 0" }}>
                    Type: <strong>{eventDetails.category}</strong>
                  </p>
                )}

                {eventDetails.reservationEnabled && (
                  <p style={{ margin: "5px 0" }}>
                    Vendor spots: <strong>Available</strong>
                  </p>
                )}

                <div style={{ marginTop: "10px" }}>
                  <button className="btn-primary" type="button" onClick={openSelectedDirections}>
                    Get Directions
                  </button>
                </div>
              </FarmMapPopupCard>
                );
              })()}
            </InfoWindow>
          )}

          {showExistingJobs && selectedFarmStand?.mapPoint && (
            <InfoWindow
              position={selectedFarmStand.mapPoint}
              onCloseClick={() => setSelectedFarmStand(null)}
            >
              <FarmMapPopupCard>
                {selectedFarmStand.standImage && (
                  <img
                    src={selectedFarmStand.standImage}
                    alt={selectedFarmStand.standName || selectedFarmStand.name || "Farm stand"}
                    style={{
                      width: "100%",
                      height: "120px",
                      objectFit: "cover",
                      borderRadius: "10px",
                      marginBottom: "8px",
                    }}
                  />
                )}

                <div style={{ marginBottom: "8px" }}>
                  <FarmMapBadge type={getFarmStandMapType(selectedFarmStand)} />
                </div>

                <div style={{ fontSize: "16px", fontWeight: "800", marginBottom: "4px" }}>
                  {selectedFarmStand.standName ||
                    selectedFarmStand.name ||
                    selectedFarmStand.businessName ||
                    "Farm stand"}
                </div>

                <p style={{ margin: "5px 0" }}>
                  {getStandPublicLocationLabel(selectedFarmStand)}
                </p>

                {selectedFarmStand.description && (
                  <p style={{ margin: "6px 0", color: "#4b5563" }}>
                    {selectedFarmStand.description}
                  </p>
                )}

                {selectedFarmStand.hoursText && (
                  <p style={{ margin: "5px 0" }}>
                    Hours: <strong>{selectedFarmStand.hoursText}</strong>
                  </p>
                )}

                <div style={{ marginTop: "10px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  <button className="btn-primary" type="button" onClick={openSelectedDirections}>
                    Get Directions
                  </button>
                  <button
                    className="btn-soft"
                    type="button"
                    onClick={() =>
                      navigate(`/stand/${selectedFarmStand.id}`)
                    }
                  >
                    {getFarmStandMapType(selectedFarmStand) === "pickup_spot"
                      ? "View Pickup Spot"
                      : "View Live Shelf"}
                  </button>
                </div>
              </FarmMapPopupCard>
            </InfoWindow>
          )}

          {showExistingJobs && selectedLandUse && selectedLandUsePosition && (
            <InfoWindow
              position={selectedLandUsePosition}
              onCloseClick={() => setSelectedLandUse(null)}
            >
              <FarmMapPopupCard>
                <div style={{ marginBottom: "8px" }}>
                  <FarmMapBadge type="land_use" />
                </div>

                <div style={{ fontSize: "16px", fontWeight: "800", marginBottom: "4px" }}>
                  {selectedLandUse.title || "Land Share"}
                </div>

                <p style={{ margin: "5px 0" }}>
                  <strong>
                    {selectedLandUse.offeringOrSeeking === "seeking" ? "Seeking" : "Offering"}
                  </strong>{" "}
                  {LAND_USE_TYPE_LABELS[selectedLandUse.landUseType] || "Land use"}
                </p>

                <p style={{ margin: "5px 0" }}>
                  Acres:{" "}
                  <strong>
                    {selectedLandUse.acreage ||
                      (getJobTotalAcres(selectedLandUse) > 0
                        ? getJobTotalAcres(selectedLandUse).toFixed(2)
                        : "Not listed")}
                  </strong>
                </p>

                <p style={{ margin: "5px 0" }}>
                  Fenced: <strong>{formatUnknownBoolean(selectedLandUse.fenced)}</strong> | Water:{" "}
                  <strong>{formatUnknownBoolean(selectedLandUse.waterAccess)}</strong>
                </p>

                <p style={{ margin: "5px 0" }}>
                  {selectedLandUse.locationText ||
                    [selectedLandUse.city, selectedLandUse.state].filter(Boolean).join(", ") ||
                    "Location available"}
                </p>

                {selectedLandUse.description && (
                  <p style={{ margin: "6px 0", color: "#4b5563" }}>
                    {selectedLandUse.description}
                  </p>
                )}

                {selectedLandUse?.selectedEntrance && (
                  <div className="entrance-info-box">
                    <strong>Land Entrance</strong>
                    <p>Area #{selectedLandUse.selectedEntrance.fieldIndex + 1}</p>
                    {selectedLandUse.selectedEntrance.note && (
                      <p>{selectedLandUse.selectedEntrance.note}</p>
                    )}
                  </div>
                )}

                <div style={{ marginTop: "10px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  <button className="btn-primary" type="button" onClick={openSelectedDirections}>
                    Get Directions
                  </button>
                  <button
                    className="btn-soft"
                    type="button"
                    onClick={() =>
                      navigate("/community", {
                        state: { typeFilter: "land_use", focusPostId: selectedLandUse.id },
                      })
                    }
                  >
                    View Post
                  </button>
                </div>
              </FarmMapPopupCard>
            </InfoWindow>
          )}

          {userLocationMarkerPosition && !isDrawing && (
            <Marker position={userLocationMarkerPosition} />
          )}

          {draftPath.length > 0 && (
            <Polygon
              path={draftPath}
              options={{
                fillColor: "#4caf50",
                fillOpacity: 0.22,
                strokeColor: "#2e7d32",
                strokeWeight: 3,
                zIndex: 80,
              }}
              editable
              draggable
              onLoad={handleEditablePolygonLoad}
              onUnmount={handleEditablePolygonUnmount}
            />
          )}

          {draftMarkerPosition && <Marker position={draftMarkerPosition} />}

          {!showExistingJobs &&
            completedFields.map((field, index) => (
              <React.Fragment key={field.id || index}>
                {Array.isArray(field.polygonPath) && field.polygonPath.length >= 3 && (
                  <Polygon
  path={field.polygonPath}
  options={{
    fillColor: editingFieldIndex === index ? "#3b82f6" : "#4caf50",
    fillOpacity: editingFieldIndex === index ? 0.24 : 0.18,
    strokeColor: editingFieldIndex === index ? "#2563eb" : "#2e7d32",
    strokeWeight: editingFieldIndex === index ? 3 : 2,
  }}
  onClick={(e) => {
    if (entranceMode && e.latLng) {
      placeEntranceAt(e.latLng.lat(), e.latLng.lng());
      return;
    }

    zoomToField(field);
  }}
/>
                )}

                {field.lat && field.lng && (
                  <Marker
  position={{ lat: field.lat, lng: field.lng }}
  icon={getMarkerIcon(field.category, editingFieldIndex === index)}
  onClick={(e) => {
    if (entranceMode && e.latLng) {
      placeEntranceAt(e.latLng.lat(), e.latLng.lng());
      return;
    }

    zoomToField(field);
  }}
/>
                )}

{(field.entrance?.lat || field.entranceLat) &&
  (field.entrance?.lng || field.entranceLng) && (
    <Marker
  position={{
    lat: field.entrance?.lat || field.entranceLat,
    lng: field.entrance?.lng || field.entranceLng,
  }}
  icon={getEntranceMarkerIcon(false)}
  title={`Entrance for Field ${index + 1}`}
/>
  )}

              </React.Fragment>
))}


            
        </GoogleMap>
      </div>
    </div>
  );
};

export default MapView;
